// Playbook edits queue — pilot-retros.md §5.
// Workflow: proposed → accepted → merged / rejected.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PlaybookEdit } from "../lib/api";

const STATUSES = ["proposed", "accepted", "merged", "rejected"];

const STATUS_COLOR: Record<string, string> = {
  proposed: "bg-slate-100 text-slate-800",
  accepted: "bg-blue-100 text-blue-900",
  merged:   "bg-emerald-100 text-emerald-900",
  rejected: "bg-rose-100 text-rose-900",
};

export function PlaybookEdits() {
  const [filter, setFilter] = useState<string>("");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PlaybookEdit[]>({
    queryKey: ["playbook-edits", filter],
    queryFn: () => api.playbookEdits.list(filter || undefined),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status, decision_notes }: { id: string; status: string; decision_notes?: string }) =>
      api.playbookEdits.update(id, { status, decision_notes } as any),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["playbook-edits"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Playbook edits</h1>
        <select className="border rounded px-2 py-1 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {isLoading ? <p>Loading…</p> : null}

      <ul className="space-y-2">
        {(data ?? []).map((e) => (
          <li key={e.id} className="border rounded p-3 bg-white">
            <div className="flex items-baseline justify-between">
              <div className="text-sm">
                <span className="font-mono">{e.target_file}</span>
                {e.section ? <span className="text-slate-500"> · {e.section}</span> : null}
                <span className="text-slate-500"> · from {e.source_pilot_name || "(orphan)"} ({e.source})</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[e.status]}`}>{e.status}</span>
            </div>
            <p className="text-sm mt-1"><b>Proposed:</b> {e.proposed_change}</p>
            {e.current_text ? <p className="text-xs text-slate-600 mt-1"><b>Current:</b> {e.current_text}</p> : null}
            {e.rationale ? <p className="text-xs text-slate-600"><b>Why:</b> {e.rationale}</p> : null}
            {e.decided_at ? (
              <p className="text-xs text-slate-500 mt-1">
                {e.status} by {e.decided_by_name} on {new Date(e.decided_at).toLocaleDateString()}
                {e.decision_notes ? <> — {e.decision_notes}</> : null}
              </p>
            ) : null}
            {e.status === "proposed" || e.status === "accepted" ? (
              <div className="mt-2 flex gap-2 text-xs">
                {e.status === "proposed" ? (
                  <button
                    className="px-2 py-1 rounded bg-blue-600 text-white"
                    onClick={() => setStatus.mutate({ id: e.id, status: "accepted" })}
                  >Accept</button>
                ) : null}
                {e.status === "accepted" ? (
                  <button
                    className="px-2 py-1 rounded bg-emerald-600 text-white"
                    onClick={() => setStatus.mutate({ id: e.id, status: "merged" })}
                  >Mark merged</button>
                ) : null}
                <button
                  className="px-2 py-1 rounded bg-rose-600 text-white"
                  onClick={() => {
                    const note = prompt("Rejection reason?");
                    if (note != null) setStatus.mutate({ id: e.id, status: "rejected", decision_notes: note });
                  }}
                >Reject</button>
              </div>
            ) : null}
          </li>
        ))}
        {(data ?? []).length === 0 && !isLoading ? (
          <li className="text-sm text-slate-500">No edits {filter ? `in status ${filter}` : "yet"}.</li>
        ) : null}
      </ul>
    </div>
  );
}
